import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "../client";
import type {
  Organization,
  OrganizationCreate,
  OrganizationListRow,
  OrganizationUpdate,
} from "../types";

const KEY = ["organizations"] as const;

export function useOrganizations() {
  return useQuery<OrganizationListRow[]>({
    queryKey: KEY,
    queryFn: () => apiRequest("/api/organizations"),
  });
}

export function useOrganization(id: string | undefined) {
  return useQuery<Organization>({
    queryKey: [...KEY, id],
    queryFn: () => apiRequest(`/api/organizations/${id}`),
    enabled: Boolean(id),
  });
}

export function useCreateOrganization() {
  const qc = useQueryClient();
  return useMutation<Organization, Error, OrganizationCreate>({
    mutationFn: (payload) =>
      apiRequest("/api/organizations", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateOrganization(id: string) {
  const qc = useQueryClient();
  return useMutation<Organization, Error, OrganizationUpdate>({
    mutationFn: (payload) =>
      apiRequest(`/api/organizations/${id}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      }),
    onSuccess: (org) => {
      qc.setQueryData([...KEY, id], org);
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useDeleteOrganization() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) =>
      apiRequest(`/api/organizations/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
